import React, { useCallback, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Paper,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-toastify';

import Loader from '../../components/generic/Loader';

const LIST_PRODUCT_CATEGORIES = gql`
	query listProductCategories {
		productCategories(includeInactive: true) {
			id
			name
			slug
			displayOrder
			isActive
		}
	}
`;

const SAVE_PRODUCT_CATEGORY = gql`
	mutation saveProductCategory($category: ProductCategoryInput!, $id: Int) {
		saveProductCategory(category: $category, id: $id) {
			id
			name
			slug
			displayOrder
			isActive
		}
	}
`;

interface Category {
	id: number;
	name: string;
	slug: string;
	displayOrder: number;
	isActive: boolean;
}

interface DraftCategory {
	id?: number;
	name: string;
	slug: string;
	displayOrder: number;
	isActive: boolean;
}

const EMPTY_DRAFT: DraftCategory = { name: '', slug: '', displayOrder: 0, isActive: true };

/**
 * The API validates the slug as lowercase words joined by single hyphens; this
 * produces that shape from a display name so it rarely has to be typed.
 */
export function toSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

function ProductCategoryListPage() {
	const { data, loading, refetch } = useQuery(LIST_PRODUCT_CATEGORIES);
	const [saveCategory, { loading: saving }] = useMutation(SAVE_PRODUCT_CATEGORY);

	const [draft, setDraft] = useState<DraftCategory | null>(null);
	// Once the slug has been typed by hand, stop overwriting it from the name.
	const [slugTouched, setSlugTouched] = useState(false);

	const categories: Category[] = data?.productCategories ?? [];

	const openNew = useCallback(() => {
		setDraft({ ...EMPTY_DRAFT, displayOrder: categories.length });
		setSlugTouched(false);
	}, [categories.length]);

	const openEdit = useCallback((category: Category) => {
		setDraft({ ...category });
		setSlugTouched(true);
	}, []);

	const close = useCallback(() => setDraft(null), []);

	const save = useCallback(async () => {
		if (!draft) {
			return;
		}
		const name = draft.name.trim();
		const slug = draft.slug.trim() || toSlug(name);

		if (!name) {
			toast.error('Enter a category name.');
			return;
		}
		if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
			toast.error('Slug must be lowercase words separated by single hyphens.');
			return;
		}

		try {
			await saveCategory({
				variables: {
					id: draft.id ?? null,
					category: { name, slug, displayOrder: draft.displayOrder, isActive: draft.isActive },
				},
			});
			toast.success(draft.id ? 'Category updated' : 'Category added');
			setDraft(null);
			await refetch();
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Could not save the category.');
		}
	}, [draft, refetch, saveCategory]);

	if (loading && categories.length === 0) {
		return <Loader isLoading />;
	}

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
				<Box>
					<Typography variant="h4" gutterBottom>
						Product Categories
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Categories group products into the filter chips customers see in the delivery app.
						Only categories with something in stock appear there.
					</Typography>
				</Box>
				<Button variant="contained" startIcon={<Add />} onClick={openNew}>
					Add category
				</Button>
			</Stack>

			<Paper>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							<TableCell>Slug</TableCell>
							<TableCell>Status</TableCell>
							<TableCell align="right">Display order</TableCell>
							<TableCell align="right">Edit</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{categories.length === 0 ? (
							<TableRow>
								<TableCell colSpan={5}>
									<Box sx={{ py: 4, textAlign: 'center' }}>
										<Typography variant="body1" gutterBottom>
											No categories yet
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Add the groups your products fall into — for example Fruit &amp; Vegetables,
											Dairy, Bakery.
										</Typography>
									</Box>
								</TableCell>
							</TableRow>
						) : (
							categories.map((category) => (
								<TableRow key={category.id} hover>
									<TableCell>{category.name}</TableCell>
									<TableCell>
										<Chip label={category.slug} size="small" variant="outlined" />
									</TableCell>
									<TableCell>
										<Chip
											label={category.isActive ? 'Active' : 'Inactive'}
											size="small"
											color={category.isActive ? 'success' : 'default'}
										/>
									</TableCell>
									<TableCell align="right">{category.displayOrder}</TableCell>
									<TableCell align="right">
										<Button size="small" startIcon={<Edit />} onClick={() => openEdit(category)}>
											Edit
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</Paper>

			<Dialog open={draft !== null} onClose={close} fullWidth maxWidth="sm">
				<DialogTitle>{draft?.id ? 'Edit category' : 'Add category'}</DialogTitle>
				<DialogContent>
					<Stack spacing={3} sx={{ mt: 1 }}>
						<TextField
							fullWidth
							required
							autoFocus
							label="Name"
							value={draft?.name ?? ''}
							onChange={(event) => {
								const name = event.target.value;
								setDraft((current) =>
									current
										? { ...current, name, slug: slugTouched ? current.slug : toSlug(name) }
										: current,
								);
							}}
							helperText="Shown to customers as a filter chip"
						/>

						<TextField
							fullWidth
							required
							label="Slug"
							value={draft?.slug ?? ''}
							onChange={(event) => {
								setSlugTouched(true);
								setDraft((current) => (current ? { ...current, slug: event.target.value } : current));
							}}
							helperText="Stable key used by the app. Renaming the category does not change it."
						/>

						<TextField
							fullWidth
							type="number"
							label="Display order"
							value={draft?.displayOrder ?? 0}
							onChange={(event) =>
								setDraft((current) =>
									current ? { ...current, displayOrder: Number(event.target.value) || 0 } : current,
								)
							}
							helperText="Lower numbers appear first. Ties fall back to name."
						/>

						<Box>
							<FormControlLabel
								control={
									<Switch
										checked={draft?.isActive ?? true}
										onChange={(event) =>
											setDraft((current) =>
												current ? { ...current, isActive: event.target.checked } : current,
											)
										}
									/>
								}
								label="Active"
							/>
							<Typography variant="caption" display="block" color="text.secondary">
								Inactive categories stay on their products but are not offered when assigning one.
							</Typography>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={close} disabled={saving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={() => void save()} disabled={saving}>
						{saving ? 'Saving…' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default ProductCategoryListPage;
